<template>
  <div>
    <div v-if="loaded">
      <router-link
        :to="{ name: 'collection-list' }"
        class="button is-secondary is-small margin-bottom-lg has-icon-left"
      >
        <span class="icon-button arrow-left" />
        Back to collections
      </router-link>
      <div class="buttons is-pulled-right">
        <b-tooltip
          label="Share collection"
          style="margin-right: 0.5rem;"
        >
          <button
            class="button margin-bottom-lg is-small is-square"
            @click.prevent="showSharingModal = !(showSharingModal)"
          >
            <span class="icon-button share" />
          </button>
        </b-tooltip>
        <b-tooltip label="Delete collection">
          <button
            class="button margin-bottom-lg is-small is-square"
            @click.prevent="showDeleteCollectionModal = true"
          >
            <span class="icon-button trash" />
          </button>
        </b-tooltip>
      </div>
      <h1 class="subtitle is-large margin-bottom-sm">
        {{ collection.name }}
      </h1>

      <p class="text color-bluegrey3 margin-bottom-lg">
        {{ collection.items.length }} items
      </p>
      <p>{{ collection.description }}</p>

      <div
        v-for="item in collection.items"
        :key="item.pk"
      >
        <track-list-item :track="item">
          <div class="buttons are-small">
            <a
              class="icon-button trash"
              @click.prevent="handleRemoveTrackFromCollection(item.pk)"
            />
          </div>
        </track-list-item>
      </div>

      <b-modal
        :active.sync="showDeleteCollectionModal"
        :width="450"
        has-modal-card
      >
        <div
          class="modal-card"
          style="width: auto"
        >
          <header class="modal-card-head is-block">
            <span class="icon-static xl large-trash" />
            <h4 class="title is-4">
              Delete {{ collection.name }}?
            </h4>
          </header>
          <footer class="modal-card-foot is-block">
            <button
              type="submit"
              class="button is-fullwidth is-primary margin-bottom is-large margin-bottom-sm"
              @click.prevent="performDeleteCollection"
            >
              Delete
            </button>
            <button
              class="button is-fullwidth is-secondary is-large"
              @click.prevent="showDeleteCollectionModal = false"
            >
              Cancel
            </button>
          </footer>
        </div>
      </b-modal>

      <b-modal
        :active.sync="showRemoveTrackModal"
        :width="450"
        has-modal-card
      >
        <div
          class="modal-card"
          style="width: auto"
        >
          <header class="modal-card-head is-block">
            <span class="icon-static xl large-trash" />
            <h4 class="title is-4">
              Remove this item?
            </h4>
          </header>
          <footer class="modal-card-foot is-block">
            <button
              type="submit"
              class="button is-fullwidth is-primary margin-bottom is-large margin-bottom-sm"
              @click.prevent="performRemoveTrackFromCollection"
            >
              Remove
            </button>
            <button
              class="button is-fullwidth is-secondary is-large"
              @click.prevent="showRemoveTrackModal = false"
            >
              Cancel
            </button>
          </footer>
        </div>
      </b-modal>

      <b-modal
        :active.sync="showSharingModal"
        has-modal-card
      >
        <div
          class="modal-card sharing-modal"
        >
          <header class="modal-card-head is-block">
            <span class="icon-static xl large-share" />
            <h4 class="title is-4">
              Collection sharing
            </h4>
          </header>
          <section class="modal-card-body">
            <b-switch
              :value="collection.is_sharing_enabled"
              type="is-success"
              @input.native="performUpdateSharingStatus($event.target.checked)"
            >
              <small v-if="collection.is_sharing_enabled">
                Disable sharing
              </small>
              <small v-else>
                Enable sharing
              </small>
            </b-switch>
            <br>
            <br>
            <div v-if="privateUrl">
              <span style="font-size: 14px;">
                <strong>URL</strong>: <a
                  :id="privateUrlHrefId"
                  :href="privateUrl"
                >{{ privateUrl.slice(0, 40) }}&hellip;</a>
              </span>
              <b-tooltip label="Copy URL to clipboard">
                <a
                  class="icon-button clipboard pull-right"
                  @click="copyPrivateUrl"
                />
              </b-tooltip>
            </div>
            <p v-else>
              <strong>URL</strong>: <em>Sharing not enabled</em>
            </p>
          </section>
          <footer class="modal-card-foot is-block">
            <button
              class="button is-fullwidth is-secondary is-large"
              @click.prevent="showSharingModal = false"
            >
              Close
            </button>
          </footer>
        </div>
      </b-modal>
    </div>
    <div
      v-if="!loaded"
      class="columns"
    >
      <div class="column is-12 has-text-centered">
        <br>
        <span
          class="loader xl"
          style="display: inline-block; width: 3em; height: 3em;"
        />
      </div>
    </div>
  </div>
</template>

<script>
import collectionsClient from '@/api/teamCollections.js'
import TrackListItem from '@/components/TrackListItem.vue'
import { copyHrefToClipboard } from '@/utils/common.js'

export default {
  name: 'CollectionDetail',

  components: {
    TrackListItem
  },

  props: {
    collectionId: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      collection: null,
      loaded: false,
      showDeleteCollectionModal: false,
      itemIdToRemove: null,
      showRemoveTrackModal: false,
      showSharingModal: false
    }
  },

  computed: {
    user: function () {
      return this.$store.getters['user/currentUser']
    },

    fullName: function () {
      return this.$store.getters['user/fullName']
    },

    privateUrlHrefId: function () {
      return `id_private_${this.collection.id}`
    },

    privateUrl: function () {
      if (this.collection.is_sharing_enabled) {
        const resolved = this.$router.resolve({
          name: 'shared-collection-detail',
          params: {
            privateCollectionId: this.collection.private_id
          }
        }, this.$route)
        return process.env.VUE_APP_BASE_URL + resolved.href
      }
      return ''
    }
  },

  created () {
    this.fetchCollection()
  },

  methods: {
    fetchCollection: function () {
      // Load this collection data.
      collectionsClient.retrieve(this.collectionId)
        .then(response => {
          this.collection = response.data
          this.loaded = true
          if (this.collection.items.length > 0) {
            this.$store.dispatch('player/loadTracks', this.collection.items)
          }
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.$router.push({ name: 'not-found' })
          }
        })
    },

    performDeleteCollection: function () {
      collectionsClient.delete(this.collection.pk).then(response => {
        this.$router.push({ name: 'collection-list' })
      })
    },

    handleRemoveTrackFromCollection: function (itemId) {
      this.itemIdToRemove = itemId
      this.showRemoveTrackModal = true
    },

    performUpdateSharingStatus: function (value) {
      collectionsClient
        .partialUpdate(this.collectionId, { is_sharing_enabled: value })
        .then(response => {
          this.collection = response.data
        })
    },

    performRemoveTrackFromCollection: function () {
      const itemIds = this.collection.items
        .map(item => item.pk)
        .filter(item => item !== this.itemIdToRemove)
      collectionsClient
        .partialUpdate(this.collectionId, { item_pks: itemIds })
        .then(response => {
          this.collection = response.data
          this.showRemoveTrackModal = false
          this.itemIdToRemove = null
        })
    },

    copyPrivateUrl: function () {
      if (copyHrefToClipboard(this.privateUrlHrefId)) {
        this.$buefy.notification.open({
          message: 'Link copied to clipboard',
          type: 'is-success'
        })
      } else {
        this.$buefy.notification.open({
          message: 'Could not copy link.',
          type: 'is-danger'
        })
      }
    }
  },

  metaInfo () {
    return {
      title: this.collection ? this.collection.name : 'Collection'
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../assets/scss/_variables.scss";

.sharing-modal {
  width: 450px;
  height: 350px;

  @media only screen and (max-width: $tablet) {
    width: 100%;
    height: auto;
  }
}
</style>
